import { DROPDOWN_TYPE, EXACT_TYPE, RANGE_TYPE } from './constants';

const hasValue = (value) => value !== '' && value !== null && value !== undefined;

export function compileFilters(filters, configuration = {}) {
  const params = {};
  const appliedFilters = [];
  const errors = [];
  const usedFields = new Set();

  filters.forEach((filter, index) => {
    const { type, var: field, value1, value2 } = filter;
    if (!type && !field && !hasValue(value1) && !hasValue(value2)) return;

    if (!type || !field || !configuration?.[type]?.[field]) {
      errors.push({ index, message: 'Select a valid filter type and field.' });
      return;
    }

    const fieldConfiguration = configuration[type][field];
    const filterKey = `${type}:${field}`;
    if (usedFields.has(filterKey)) {
      errors.push({ index, message: `Filter ${field} is selected more than once.` });
      return;
    }

    if (type === EXACT_TYPE || type === DROPDOWN_TYPE) {
      if (!hasValue(value1)) {
        if (type === DROPDOWN_TYPE) return;
        errors.push({ index, message: `Enter a value for ${field}.` });
        return;
      }

      if (fieldConfiguration.valType === 'number' && !Number.isFinite(Number(value1))) {
        errors.push({ index, message: `Enter a valid number for ${field}.` });
        return;
      }

      if (fieldConfiguration.params && !fieldConfiguration.params.value) {
        errors.push({ index, message: `Filter ${field} has an invalid parameter override.` });
        return;
      }

      const parameter = fieldConfiguration.params?.value || field;
      if (Object.prototype.hasOwnProperty.call(params, parameter)) {
        errors.push({ index, message: `Filter ${field} conflicts with another selected filter.` });
        return;
      }

      usedFields.add(filterKey);
      params[parameter] = value1;
      appliedFilters.push({ ...filter });
      return;
    }

    if (type !== RANGE_TYPE) {
      errors.push({ index, message: `Filter type ${type} is not supported.` });
      return;
    }

    const hasMinimum = hasValue(value1);
    const hasMaximum = hasValue(value2);
    if (!hasMinimum && !hasMaximum) {
      errors.push({ index, message: `Enter at least one value for ${field}.` });
      return;
    }

    if (fieldConfiguration.params && (!fieldConfiguration.params.min || !fieldConfiguration.params.max)) {
      errors.push({ index, message: `Filter ${field} has an invalid parameter override.` });
      return;
    }

    if (fieldConfiguration.valType === 'number') {
      if ((hasMinimum && !Number.isFinite(Number(value1))) || (hasMaximum && !Number.isFinite(Number(value2)))) {
        errors.push({ index, message: `Enter valid numbers for ${field}.` });
        return;
      }
      if (hasMinimum && hasMaximum && Number(value1) > Number(value2)) {
        errors.push({ index, message: `The minimum for ${field} cannot exceed the maximum.` });
        return;
      }
    }

    if (fieldConfiguration.valType === 'date' && hasMinimum && hasMaximum && value1 > value2) {
      errors.push({ index, message: `The start date for ${field} cannot exceed the end date.` });
      return;
    }

    const minimumParameter = fieldConfiguration.params?.min || `${field}_after`;
    const maximumParameter = fieldConfiguration.params?.max || `${field}_before`;
    const parameters = [hasMinimum && minimumParameter, hasMaximum && maximumParameter].filter(Boolean);
    if (new Set(parameters).size !== parameters.length || parameters.some((parameter) => Object.prototype.hasOwnProperty.call(params, parameter))) {
      errors.push({ index, message: `Filter ${field} conflicts with another selected filter.` });
      return;
    }

    usedFields.add(filterKey);
    if (hasMinimum) params[minimumParameter] = value1;
    if (hasMaximum) params[maximumParameter] = value2;
    appliedFilters.push({ ...filter });
  });

  return { params, appliedFilters, errors };
}
